import React, { useState, useEffect } from 'react';
import { Heart, Mail, MapPin, Loader2, Globe, Phone } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { footerData, contactData } from '../data/mock';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { toast } from 'sonner';

const Footer = () => {
  const location = useLocation();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const languages = [
    { code: 'fr', label: 'Francais', flag: 'FR' },
    { code: 'en', label: 'English', flag: 'EN' },
    { code: 'es', label: 'Espanol', flag: 'ES' },
  ];
  const [currentLang, setCurrentLang] = useState('fr');

  useEffect(() => {
    const addScript = () => {
      if (document.getElementById('google-translate-script')) return;
      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      document.body.appendChild(script);
      window.googleTranslateElementInit = () => {
        new window.google.translate.TranslateElement(
          { pageLanguage: 'fr', autoDisplay: false },
          'google_translate_element'
        );
      };
    };
    addScript();
  }, []);

  const changeLanguage = (langCode) => {
    setCurrentLang(langCode);
    const frame = document.querySelector('.goog-te-menu-frame');
    if (frame) {
      const innerDoc = frame.contentDocument || frame.contentWindow.document;
      const items = innerDoc.querySelectorAll('.goog-te-menu2-item span.text');
      items.forEach(item => {
        if (
          (langCode === 'en' && item.textContent === 'English') ||
          (langCode === 'es' && item.textContent === 'Spanish') ||
          (langCode === 'fr' && item.textContent === 'French')
        ) item.click();
      });
    } else {
      const domain = window.location.hostname;
      if (langCode === 'fr') {
        document.cookie = `googtrans=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
        document.cookie = `googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
        window.location.reload();
      } else {
        document.cookie = `googtrans=/fr/${langCode}; path=/; domain=${domain}`;
        document.cookie = `googtrans=/fr/${langCode}; path=/`;
        window.location.reload();
      }
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    setSubscribing(true);
    try {
      await axios.post(API_ENDPOINTS.newsletter, { email: newsletterEmail });
      toast.success('Inscription reussie! Merci de nous suivre.');
      setNewsletterEmail('');
    } catch (error) {
      if (error.response?.status === 400) toast.info('Cet email est deja inscrit.');
      else toast.error("Erreur lors de l'inscription. Veuillez reessayer.");
    } finally {
      setSubscribing(false);
    }
  };

  const handleLinkClick = (e, link) => {
    if (link.url.startsWith('/#')) {
      e.preventDefault();
      const sectionId = link.url.substring(2);
      if (location.pathname === '/') {
        const element = document.querySelector(`#${sectionId}`);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.location.href = link.url;
      }
    }
  };

  return (
    <footer className="bg-navy text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center space-x-3 mb-6">
              <img src="/images/logo-hpd.png" alt="HPD Logo" className="h-12 w-12 object-contain" />
              <div>
                <h3 className="font-serif font-bold text-xl notranslate" translate="no">Hope for Peace</h3>
                <p className="text-sm text-white/50 font-sans">& Development</p>
              </div>
            </Link>
            <p className="text-white/60 leading-relaxed text-sm font-sans mb-6">{footerData.about}</p>
            <div className="flex items-center space-x-2 text-forest text-sm font-sans">
              <Heart className="w-4 h-4" fill="currentColor" />
              <span className="font-medium">Autonomiser - Proteger - Developper</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-serif font-bold text-lg mb-6">Liens Rapides</h3>
            <ul className="space-y-3">
              {footerData.quickLinks.map((link) => (
                <li key={link.id}>
                  {link.url.startsWith('/#') ? (
                    <a href={link.url} onClick={(e) => handleLinkClick(e, link)} className="text-white/60 hover:text-forest transition-colors text-sm font-sans">
                      {link.label}
                    </a>
                  ) : (
                    <Link to={link.url} className="text-white/60 hover:text-forest transition-colors text-sm font-sans">
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
              <li>
                <Link to="/don" className="text-forest hover:text-forest/80 transition-colors text-sm font-sans font-semibold">
                  Faire un don
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-serif font-bold text-lg mb-6">Contact</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="w-4 h-4 text-forest flex-shrink-0 mt-1" />
                <span className="text-white/60 text-sm font-sans">Bunia, Ituri, RDC</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-4 h-4 text-forest flex-shrink-0" />
                <a href={`mailto:${contactData.info.email}`} className="text-white/60 text-sm font-sans hover:text-forest transition-colors">{contactData.info.email}</a>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-4 h-4 text-forest flex-shrink-0" />
                <span className="text-white/60 text-sm font-sans">{contactData.info.phone}</span>
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="font-serif font-bold text-lg mb-6">Restez Informe</h3>
            <p className="text-white/60 text-sm font-sans mb-4">Recevez nos dernieres actualites et initiatives</p>
            <form onSubmit={handleNewsletterSubmit} className="flex">
              <input
                type="email"
                placeholder="Votre email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                required
                data-testid="newsletter-email-input"
                className="flex-1 px-4 py-3 rounded-l-full bg-white/10 border border-white/10 text-white text-sm focus:outline-none focus:border-forest placeholder:text-white/40 font-sans"
              />
              <button
                type="submit"
                disabled={subscribing}
                data-testid="newsletter-submit-btn"
                className="px-5 py-3 bg-forest hover:bg-forest/80 rounded-r-full transition-colors disabled:opacity-50"
              >
                {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              </button>
            </form>
          </div>
        </div>

        {/* Language + Bottom */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-1 bg-white/5 rounded-full p-1">
              <Globe className="w-4 h-4 text-white/40 mx-2" />
              {languages.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  data-testid={`lang-${lang.code}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium font-sans transition-all ${
                    currentLang === lang.code ? 'bg-forest text-white' : 'text-white/50 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {lang.flag} {lang.label}
                </button>
              ))}
            </div>
          </div>
          <div id="google_translate_element" style={{ display: 'none' }} />

          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-white/40 font-sans">{footerData.copyright}</p>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-white/40 font-sans">
                <span>Fait avec</span>
                <Heart className="w-3 h-3 text-red-400" fill="currentColor" />
                <span>pour un avenir meilleur</span>
              </div>
              <Link to="/admin/login" className="text-xs text-white/20 hover:text-white/40 transition-colors font-sans" data-testid="admin-link">
                Admin
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
